import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Twitter, Facebook, Youtube, Linkedin, X } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const AppStoreBadge = ({ onClick }) => (
  <button onClick={onClick} className="hover:opacity-90 transition-opacity">
    <svg viewBox="0 0 120 40" className="h-10 w-auto" xmlns="http://www.w3.org/2000/svg">
      <rect width="120" height="40" rx="6" fill="#000"/>
      <rect x="0.5" y="0.5" width="119" height="39" rx="5.5" stroke="#A6A6A6" fill="none"/>
      <path d="M24.77 20.3c-.02-2.17 1.77-3.22 1.85-3.27a5.07 5.07 0 00-3.99-2.15c-1.68-.18-3.3 1-4.15 1s-2.18-1-3.58-1a5.28 5.28 0 00-4.45 2.72c-1.9 3.3-.49 8.18 1.36 10.86.9 1.31 1.98 2.79 3.38 2.74 1.36-.06 1.87-.88 3.52-.88s2.11.88 3.55.85c1.46-.03 2.4-1.33 3.28-2.65a10.95 10.95 0 001.49-3.05 4.78 4.78 0 01-2.26-4.17z" fill="#fff"/>
      <path d="M22.15 12.21a4.88 4.88 0 001.12-3.5 4.97 4.97 0 00-3.22 1.67 4.64 4.64 0 00-1.14 3.37 4.12 4.12 0 003.24-1.54z" fill="#fff"/>
      <text x="42" y="14" fill="#fff" fontSize="6.5" fontFamily="system-ui, sans-serif">Download on the</text>
      <text x="42" y="27" fill="#fff" fontSize="13" fontWeight="600" fontFamily="system-ui, sans-serif">App Store</text>
    </svg>
  </button>
);

const PlayStoreBadge = ({ onClick }) => (
  <button onClick={onClick} className="hover:opacity-90 transition-opacity">
    <svg viewBox="0 0 135 40" className="h-10 w-auto" xmlns="http://www.w3.org/2000/svg">
      <rect width="135" height="40" rx="6" fill="#000"/>
      <rect x="0.5" y="0.5" width="134" height="39" rx="5.5" stroke="#A6A6A6" fill="none"/>
      {/* Play triangle icon */}
      <defs>
        <linearGradient id="playGrad" x1="12" y1="8" x2="12" y2="32" gradientUnits="userSpaceOnUse">
          <stop stopColor="#00C3FF"/>
          <stop offset="0.5" stopColor="#4DEF8E"/>
          <stop offset="1" stopColor="#FFBC00"/>
        </linearGradient>
      </defs>
      <path d="M11 8.5l16 11.5-16 11.5V8.5z" fill="url(#playGrad)"/>
      <path d="M11 8.5l10 7.5 6 4-6 4-10 7.5V8.5z" fill="url(#playGrad)" opacity="0.9"/>
      <text x="34" y="14" fill="#A0A0A0" fontSize="6" fontFamily="system-ui, sans-serif" textTransform="uppercase">GET IT ON</text>
      <text x="34" y="27" fill="#fff" fontSize="13" fontWeight="600" fontFamily="system-ui, sans-serif">Google Play</text>
    </svg>
  </button>
);

const WaitlistModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (res.ok) {
        setSubmitted(true);
        setEmail('');
      }
    } catch {
      // silent fail
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 rounded-2xl max-w-md w-full p-8 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Logo mark */}
        <div className="w-16 h-16 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-6">
          <div className="w-8 h-8 border-3 border-white rounded-full" style={{ borderWidth: '3px' }}></div>
        </div>

        {submitted ? (
          <div className="text-center">
            <h2 className="text-2xl font-bold text-white mb-3">You're on the list!</h2>
            <p className="text-blue-200 text-sm">We'll let you know as soon as the Wanderloop app is ready.</p>
          </div>
        ) : (
          <>
            <h2 className="text-2xl font-bold text-white text-center mb-2">Be first to explore.</h2>
            <p className="text-blue-200 text-center text-sm mb-6">
              Get notified when the Wanderloop app launches.
            </p>

            <form onSubmit={handleSubmit} className="space-y-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-amber-400/60 text-sm"
              />
              <button
                type="submit"
                disabled={submitting}
                className="w-full px-4 py-3 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black font-bold rounded-lg transition-all disabled:opacity-50 text-sm"
              >
                {submitting ? 'Joining...' : 'Notify Me'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [showWaitlist, setShowWaitlist] = useState(false);

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Explore Section */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Explore</h3>
            <ul className="space-y-2 text-gray-300">
              <li><Link to="/walks?category=Nature" className="hover:text-white transition-colors">Nature & Parks</Link></li>
              <li><Link to="/walks?category=Hidden Gems" className="hover:text-white transition-colors">Hidden Gems</Link></li>
              <li><Link to="/walks?category=Architecture" className="hover:text-white transition-colors">Architecture</Link></li>
              <li><Link to="/walks?category=Food & Drink" className="hover:text-white transition-colors">Food Walks</Link></li>
              <li><Link to="/walks?category=Historic" className="hover:text-white transition-colors">Historic Walks</Link></li>
              <li><Link to="/walks?category=Waterfront" className="hover:text-white transition-colors">Waterfront</Link></li>
              <li><Link to="/walks" className="hover:text-white transition-colors">Browse All Walks</Link></li>
            </ul>
          </div>

          {/* Resources Section */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Resources</h3>
            <ul className="space-y-2 text-gray-300">
              <li><Link to="/membership" className="hover:text-white transition-colors">Join Wanderloop+</Link></li>
              <li><Link to="/submit" className="hover:text-white transition-colors">Submit a Walk</Link></li>
              <li><Link to="/about" className="hover:text-white transition-colors">About Wanderloop</Link></li>
              <li><Link to="/contact" className="hover:text-white transition-colors">Contact Us</Link></li>
              <li><Link to="/tips" className="hover:text-white transition-colors">Walking Tips</Link></li>
              <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
              <li><Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link></li>
            </ul>
          </div>

          {/* Community Section */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Community</h3>
            <ul className="space-y-2 text-gray-300">
              <li><Link to="/submit" className="hover:text-white transition-colors">Submit a Walk</Link></li>
              <li><Link to="/journals" className="hover:text-white transition-colors">Walking Journal</Link></li>
              <li><Link to="/partnerships" className="hover:text-white transition-colors">Partnerships</Link></li>
            </ul>
          </div>

          {/* Connect Section */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Connect with us</h3>
            <div className="flex flex-wrap gap-3 mb-6">
              <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-gray-700 transition-colors">
                <Instagram className="h-5 w-5" />
              </a>
              <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-gray-700 transition-colors">
                <Twitter className="h-5 w-5" />
              </a>
              <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-gray-700 transition-colors">
                <Facebook className="h-5 w-5" />
              </a>
              <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-gray-700 transition-colors">
                <Youtube className="h-5 w-5" />
              </a>
              <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-gray-700 transition-colors">
                <Linkedin className="h-5 w-5" />
              </a>
            </div>

            {/* App Coming Soon */}
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-3">App Coming Soon</p>
              <div className="flex gap-2">
                <AppStoreBadge onClick={() => setShowWaitlist(true)} />
                <PlayStoreBadge onClick={() => setShowWaitlist(true)} />
              </div>
            </div>
          </div>
        </div>

        {/* Mission Statement */}
        <div className="border-t border-gray-800 pt-8 mb-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="flex items-center justify-center gap-2 mb-3">
              <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center">
                <div className="w-3 h-3 border-2 border-gray-900 rounded-full"></div>
              </div>
              <span className="text-xl font-bold">WANDERLOOP</span>
            </div>
            <p className="text-gray-300 text-sm leading-relaxed">
              Mindful walking experiences that connect you to the soul of your city.
              Discover hidden gems, embrace slower travel, and find wellness through urban exploration.
            </p>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-gray-800 pt-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-gray-400 text-xs">
              <p>&copy; {currentYear} Wanderloop, LLC</p>
              <p className="mt-1">
                WANDERLOOP&trade; and the Wanderloop Loop Design are trademarks of Wanderloop, LLC.
              </p>
              <p className="mt-1 max-w-2xl">
                Wanderloop is a participant in the Amazon Services LLC Associates Program, an affiliate advertising program. As an Amazon Associate, Wanderloop earns from qualifying purchases.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-4 text-xs text-gray-400">
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <span>&middot;</span>
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>
      </div>

      {/* Waitlist Modal */}
      <WaitlistModal isOpen={showWaitlist} onClose={() => setShowWaitlist(false)} />
    </footer>
  );
};

export default Footer;
